<script setup lang="ts">
import { computed, onMounted, onUnmounted, provide, ref, watch } from 'vue'
import { useSongStore } from '../../stores/songStore'
import { useUserLibraryStore } from '../../stores/userLibraryStore'
import { useBandStore } from '../../stores/bandStore'
import { useMixStore, pixelsPerSecond, STATIC_BASE_URL } from '../../stores/mixStore'
import { usePresenceStore } from '../../stores/presenceStore'
import { useUiStore } from '../../stores/uiStore'
import type { MixDto, MixTrackDto } from '../../dtos'
import { audioEngine } from '../../audio/AudioEngine'
import MixerChannel from './MixerChannel.vue'
import TimelineTrack from './TimelineTrack.vue'
import TrackMeter from './TrackMeter.vue'

const props = defineProps<{
  mix: MixDto
}>()

const emit = defineEmits(['back'])

const songStore = useSongStore()
const bandStore = useBandStore()
const mixStore = useMixStore()
const presenceStore = usePresenceStore()
const uiStore = useUiStore()

const song = computed(() => songStore.currentSong)
const mixDetails = computed(() => mixStore.currentMix)
const isPlaying = computed(() => mixStore.isPlaying)
const isRecording = computed(() => mixStore.isRecording)
const armedTrackId = computed(() => mixStore.armedTrackId)
const currentTime = computed(() => mixStore.currentTime)
const masterLevel = computed(() => mixStore.masterLevel)

const masterDb = computed(() => {
  if (masterLevel.value <= 0.001) return '-∞'
  const db = 20 * Math.log10(masterLevel.value)
  return db > 0 ? `+${db.toFixed(1)}` : db.toFixed(1)
})

const mixerVisible = ref(false)
const newTrackName = ref('')
const addingTrack = ref(false)
const isHorizontal = ref(window.innerWidth >= 1024)
const showRenameTrack = ref(false)
const renameTrackId = ref<number | null>(null)
const renameTrackName = ref('')
const isRenaming = ref(false)
const razorMode = ref(false)
provide('razorMode', razorMode)
const snapToBars = ref(false)
provide('snapToBars', snapToBars)

const showDeleteTrackConfirm = ref(false)
const trackToDelete = ref<any>(null)

const confirmDeleteTrack = async (track: any) => {
  // Check concurrency before showing confirm dialog
  const isLocked = await presenceStore.checkConcurrency('Mix', props.mix.id)
  if (isLocked) {
    uiStore.triggerConcurrencyError('Cannot delete tracks while other members are active in this mix.')
    return
  }

  trackToDelete.value = track
  showDeleteTrackConfirm.value = true
}

const handleDeleteTrack = async () => {
  if (!trackToDelete.value) return
  try {
    await mixStore.deleteTrack(trackToDelete.value.id)
    showDeleteTrackConfirm.value = false
    trackToDelete.value = null
  } catch (error) {
    console.error(error)
  }
}

const openRenameTrack = (trackId: number, currentName: string) => {
  renameTrackId.value = trackId
  renameTrackName.value = currentName
  showRenameTrack.value = true
}

const handleRenameTrack = async () => {
  if (!renameTrackName.value || renameTrackId.value === null) return
  isRenaming.value = true
  try {
    await mixStore.updateTrack(renameTrackId.value, { name: renameTrackName.value })
    showRenameTrack.value = false
  } finally {
    isRenaming.value = false
  }
}

const showMixInfo = ref(false)
const editMixName = ref('')
const editMixMins = ref('00')
const editMixSecs = ref('00')
const editMixBpm = ref('120')
const editMixTimeSig = ref('4/4')
const dialogMetronomeOn = ref(false)
const isSavingMixInfo = ref(false)

const MAX_MIX_SECONDS = 3600
const MIN_MIX_SECONDS = 10
const MIN_BPM = 20
const MAX_BPM = 300

const mixSecondsToFields = (totalSeconds: number) => {
  const t = Math.max(0, Math.round(totalSeconds))
  editMixMins.value = Math.floor(t / 60).toString().padStart(2, '0')
  editMixSecs.value = (t % 60).toString().padStart(2, '0')
}

const mixFieldsToSeconds = (): number => {
  const m = Number(editMixMins.value) || 0
  const s = Number(editMixSecs.value) || 0
  return m * 60 + s
}

const clampMixSeconds = (n: number) => Math.max(MIN_MIX_SECONDS, Math.min(MAX_MIX_SECONDS, n))
const clampBpm = (n: number) => Math.max(MIN_BPM, Math.min(MAX_BPM, n))

const handleMixDurationFieldBlur = () => {
  mixSecondsToFields(clampMixSeconds(mixFieldsToSeconds()))
}

const nudgeMixField = (field: 'mins' | 'secs', delta: number) => {
  const step = field === 'mins' ? 60 : 1
  mixSecondsToFields(clampMixSeconds(mixFieldsToSeconds() + delta * step))
}

const nudgeBpm = (delta: number) => {
  editMixBpm.value = String(clampBpm((Number(editMixBpm.value) || 120) + delta))
}

const handleBpmBlur = () => {
  editMixBpm.value = String(clampBpm(Number(editMixBpm.value) || 120))
}

const restartDialogMetronome = () => {
  audioEngine.startMetronome(clampBpm(Number(editMixBpm.value) || 120), parseBeatsPerBar(editMixTimeSig.value), 0)
}

const stopDialogMetronome = () => {
  audioEngine.stopMetronome()
  // If the playback metronome was supposed to be running, restore it
  if (mixStore.metronomeEnabled && mixStore.isPlaying) {
    audioEngine.startMetronome(mixStore.metronomeBpm, mixStore.metronomeBeatsPerBar, mixStore.currentTime)
  }
}

const toggleDialogMetronome = () => {
  dialogMetronomeOn.value = !dialogMetronomeOn.value
  if (dialogMetronomeOn.value) restartDialogMetronome()
  else stopDialogMetronome()
}

watch([editMixBpm, editMixTimeSig], () => {
  if (dialogMetronomeOn.value) restartDialogMetronome()
})

const openMixInfo = () => {
  editMixName.value = props.mix.title
  mixSecondsToFields(mixStore.currentMix?.durationSeconds ?? 300)
  editMixBpm.value = String(mixStore.currentMix?.bpm ?? 120)
  editMixTimeSig.value = mixStore.currentMix?.timeSignature || '4/4'
  showMixInfo.value = true
}

watch(showMixInfo, (open) => {
  if (!open && dialogMetronomeOn.value) {
    dialogMetronomeOn.value = false
    stopDialogMetronome()
  }
})

const handleMixInfoSave = async () => {
  const trimmed = editMixName.value.trim()
  if (!trimmed) return
  isSavingMixInfo.value = true
  try {
    await mixStore.updateMix({
      title: trimmed,
      durationSeconds: clampMixSeconds(mixFieldsToSeconds()),
      bpm: clampBpm(Number(editMixBpm.value) || 120),
      timeSignature: editMixTimeSig.value.trim() || '4/4'
    })
    showMixInfo.value = false
  } catch (e) {
    console.error(e)
  } finally {
    isSavingMixInfo.value = false
  }
}


const handleResize = () => {
  isHorizontal.value = window.innerWidth >= 1024
}

const isDraggingCursor = ref(false)
const hoverTime = ref<number | null>(null)
const hoverPos = ref(0)
const lastMousePos = ref(0)
const scrollLeft = ref(0)
const scrollTop = ref(0)
const viewportWidth = ref(0)
const viewportHeight = ref(0)

const PHONE_TRACK_WIDTH = 64
const DESKTOP_TRACK_HEIGHT = 96

const tracksContentLength = computed(() => {
  const n = mixDetails.value?.tracks.length ?? 0
  if (n === 0) return null
  return n * (isHorizontal.value ? DESKTOP_TRACK_HEIGHT : PHONE_TRACK_WIDTH)
})

// Lines span the larger of (track-stack length) and (viewport size) so they
// always reach across the visible timeline even when only one track is loaded.
const lineSpanPixels = computed(() => {
  const content = tracksContentLength.value ?? 0
  const viewport = isHorizontal.value ? viewportHeight.value : viewportWidth.value
  return Math.max(content, viewport)
})

const cursorLineStyle = computed(() => {
  if (isHorizontal.value) {
    return { left: `${currentTime.value * pixelsPerSecond.value}px`, top: '0px', height: `${lineSpanPixels.value}px` }
  }
  return { top: `${currentTime.value * pixelsPerSecond.value}px`, left: '0px', width: `${lineSpanPixels.value}px` }
})

const hoverLineStyle = computed(() => {
  if (isHorizontal.value) {
    return { left: `${hoverPos.value}px`, top: '0px', height: `${lineSpanPixels.value}px` }
  }
  return { top: `${hoverPos.value}px`, left: '0px', width: `${lineSpanPixels.value}px` }
})

// Handle and labels need to stay in viewport regardless of scroll.
const cursorHandleStyle = computed(() => {
  if (isHorizontal.value) {
    return { top: `${scrollTop.value + 4}px`, left: `${currentTime.value * pixelsPerSecond.value}px` }
  }
  return { left: `${scrollLeft.value + 4}px`, top: `${currentTime.value * pixelsPerSecond.value}px` }
})

const CURSOR_LABEL_VERTICAL_OFFSET = 40
// In vertical (phone/tablet) mode the label sits just below the cursor line
// instead of above. Centered translate (-translate-y-1/2) makes label center
// land on `top`, so this puts the label's top edge a few px below the line.
const VERTICAL_MODE_LABEL_BELOW_OFFSET = 16
const cursorLabelStyle = computed(() => {
  if (isHorizontal.value) {
    return { top: `${scrollTop.value + viewportHeight.value / 2 - CURSOR_LABEL_VERTICAL_OFFSET}px`, left: `${currentTime.value * pixelsPerSecond.value}px` }
  }
  return { left: `${scrollLeft.value + viewportWidth.value / 2}px`, top: `${currentTime.value * pixelsPerSecond.value + VERTICAL_MODE_LABEL_BELOW_OFFSET}px` }
})

const hoverLabelStyle = computed(() => {
  if (isHorizontal.value) {
    return { top: `${scrollTop.value + viewportHeight.value / 2 - CURSOR_LABEL_VERTICAL_OFFSET}px`, left: `${hoverPos.value}px` }
  }
  return { left: `${scrollLeft.value + viewportWidth.value / 2}px`, top: `${hoverPos.value + VERTICAL_MODE_LABEL_BELOW_OFFSET}px` }
})


watch(currentTime, (newTime) => {
  if (!isPlaying.value || isDraggingCursor.value) return

  const container = document.getElementById('tracks-container')
  if (!container) return

  const cursorPosition = newTime * pixelsPerSecond.value
  const padding = 100 // pixels from edge

  if (isHorizontal.value) {
    const containerWidth = container.clientWidth
    const currentScroll = container.scrollLeft
    const relativePos = cursorPosition - currentScroll

    // If cursor moves beyond (containerWidth - padding), scroll to keep it there
    if (relativePos > containerWidth - padding) {
      container.scrollLeft = cursorPosition - containerWidth + padding
    }
    // If user seeks backward or we start before current scroll, we might want to jump scroll back
    // but typically we only auto-scroll forward during playback.
  } else {
    // Vertical timeline (Mobile)
    const containerHeight = container.clientHeight
    const currentScroll = container.scrollTop
    const relativePos = cursorPosition - currentScroll

    if (relativePos > containerHeight - padding) {
      container.scrollTop = cursorPosition - containerHeight + padding
    }
  }
})

function parseBeatsPerBar(ts?: string | null): number {
  if (!ts) return 4
  const top = parseInt(ts.split('/')[0] ?? '4', 10)
  return isNaN(top) ? 4 : top
}

// Watch the BPM + time-signature properties directly. mixStore.updateMix mutates
// currentMix in-place (to preserve .tracks on MixDetailsDto), so a reference watch
// would miss BPM saves until the user navigates away and back.
watch(
  () => ({ bpm: mixStore.currentMix?.bpm, ts: mixStore.currentMix?.timeSignature }),
  ({ bpm, ts }) => {
    if (bpm == null) return
    mixStore.setMetronomeSettings(bpm || 120, parseBeatsPerBar(ts))
  },
  { immediate: true }
)

const metronomeOn = computed(() => mixStore.metronomeEnabled)

const handleKeydown = (e: KeyboardEvent) => {
  if (e.code === 'Space' && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
    e.preventDefault()
    togglePlay()
  }
}

let viewportObserver: ResizeObserver | null = null

const measureViewport = (el: HTMLElement) => {
  viewportWidth.value = el.clientWidth
  viewportHeight.value = el.clientHeight
}

const observeViewport = () => {
  const el = document.getElementById('tracks-container')
  if (!el) return
  measureViewport(el)
  viewportObserver?.disconnect()
  viewportObserver = new ResizeObserver(() => measureViewport(el))
  viewportObserver.observe(el)
}

onMounted(async () => {
  await mixStore.fetchMixDetails(props.mix.id)
  window.addEventListener('mousemove', handleMouseMove)
  window.addEventListener('mouseup', handleMouseUp)
  window.addEventListener('resize', handleResize)
  window.addEventListener('keydown', handleKeydown)
  // Observe whichever tracks-container is currently rendered (phone or desktop layout)
  observeViewport()
})

watch(isHorizontal, () => observeViewport(), { flush: 'post' })

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove)
  window.removeEventListener('mouseup', handleMouseUp)
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('keydown', handleKeydown)
  viewportObserver?.disconnect()
})

const handleBack = () => {
  mixStore.stop()
  emit('back')
}

const handleMouseDownCursor = (e: MouseEvent) => {
  e.preventDefault()
  isDraggingCursor.value = true
}

const handleMouseMove = (e: MouseEvent) => {
  if (isDraggingCursor.value) {
    const container = document.getElementById('tracks-container')
    if (!container) return
    const rect = container.getBoundingClientRect()

    if (isHorizontal.value) {
      const x = e.clientX - rect.left + container.scrollLeft
      mixStore.seek(Math.max(0, x / pixelsPerSecond.value))
    } else {
      const y = e.clientY - rect.top + container.scrollTop
      mixStore.seek(Math.max(0, y / pixelsPerSecond.value))
    }
  }
}

const handleMouseMoveTimeline = (e: MouseEvent) => {
  // Hover indicator is desktop-only — touch devices don't have a hover state,
  // and emulated mouse events from taps would cause a stray indicator.
  if (!isHorizontal.value) return
  const container = document.getElementById('tracks-container')
  if (!container) return
  const rect = container.getBoundingClientRect()

  lastMousePos.value = e.clientX - rect.left
  hoverPos.value = lastMousePos.value + container.scrollLeft
  hoverTime.value = Math.max(0, hoverPos.value / pixelsPerSecond.value)
}

const handleMouseLeaveTimeline = () => {
  hoverTime.value = null
}

const handleMouseUp = () => {
  isDraggingCursor.value = false
}

const togglePlay = () => {
  if (isRecording.value) return
  if (isPlaying.value) {
    mixStore.stop()
  } else {
    mixStore.play()
  }
}

const toggleRecord = () => {
  mixStore.toggleRecord()
}

const downloading = ref(false)
const downloadingTrackId = ref<number | null>(null)
const showTrackDownloadDialog = ref(false)
const trackPendingDownload = ref<MixTrackDto | null>(null)

// Builds the base filename for any export (mix, track, MIDI) by joining
// song title + mix title + optional extra (track name) with underscores.
// Each part is slugified so the result is filesystem-safe.
function buildExportBaseName(extra?: string): string {
  const slug = (s: string | undefined | null) => (s || '').replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase()
  return [song.value?.title, mixDetails.value?.title, extra]
    .map(slug)
    .filter(Boolean)
    .join('_')
}

function trackHasMidi(track: MixTrackDto): boolean {
  return track.clips.some(c => {
    if (!c.isAiGenerated || !c.activePromptId) return false
    const active = c.prompts.find(p => p.id === c.activePromptId)
    return !!active?.geminiResponse
  })
}

const handleDownloadTrack = (track: MixTrackDto) => {
  if (!mixDetails.value || downloadingTrackId.value !== null) return
  if (trackHasMidi(track)) {
    trackPendingDownload.value = track
    showTrackDownloadDialog.value = true
  } else {
    void downloadTrackAsWav(track)
  }
}

const downloadTrackAsWav = async (track: MixTrackDto) => {
  if (!mixDetails.value) return
  downloadingTrackId.value = track.id
  try {
    const durationSeconds = mixDetails.value.durationSeconds ?? 0
    if (durationSeconds <= 0) return
    const blob = await audioEngine.renderTrackOffline(track, durationSeconds, STATIC_BASE_URL)
    const suggestedName = `${buildExportBaseName(track.name)}.wav`

    const showPicker = (window as any).showSaveFilePicker
    if (typeof showPicker === 'function') {
      try {
        const handle = await showPicker({
          suggestedName,
          types: [{ description: 'WAV file', accept: { 'audio/wav': ['.wav'] } }],
        })
        const writable = await handle.createWritable()
        await writable.write(blob)
        await writable.close()
        return
      } catch (e: any) {
        if (e?.name === 'AbortError') return
        console.error('showSaveFilePicker failed, falling back to direct download:', e)
      }
    }

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = suggestedName
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Failed to render track:', error)
  } finally {
    downloadingTrackId.value = null
  }
}

const downloadTrackAsMidi = async (track: MixTrackDto) => {
  if (!mixDetails.value) return
  downloadingTrackId.value = track.id
  try {
    const midiClips = track.clips
      .map(c => {
        if (!c.isAiGenerated || !c.activePromptId) return null
        const active = c.prompts.find(p => p.id === c.activePromptId)
        if (!active?.geminiResponse) return null
        return { timelineStart: c.timelineStart, midiJson: active.geminiResponse }
      })
      .filter((x): x is { timelineStart: number; midiJson: string } => x !== null)

    const { buildTrackMidiFile } = await import('../../utils/midi')
    const bytes = buildTrackMidiFile(midiClips, mixDetails.value.bpm)
    const blob = new Blob([bytes as BlobPart], { type: 'audio/midi' })
    const suggestedName = `${buildExportBaseName(track.name)}.mid`

    const showPicker = (window as any).showSaveFilePicker
    if (typeof showPicker === 'function') {
      try {
        const handle = await showPicker({
          suggestedName,
          types: [{ description: 'MIDI file', accept: { 'audio/midi': ['.mid'] } }],
        })
        const writable = await handle.createWritable()
        await writable.write(blob)
        await writable.close()
        return
      } catch (e: any) {
        if (e?.name === 'AbortError') return
        console.error('showSaveFilePicker failed, falling back to direct download:', e)
      }
    }

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = suggestedName
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Failed to build MIDI:', error)
  } finally {
    downloadingTrackId.value = null
  }
}

const handleTrackDownloadChoice = async (choice: 'midi' | 'wav') => {
  const t = trackPendingDownload.value
  showTrackDownloadDialog.value = false
  trackPendingDownload.value = null
  if (!t) return
  if (choice === 'midi') await downloadTrackAsMidi(t)
  else await downloadTrackAsWav(t)
}

const handleDownloadMix = async () => {
  if (!mixDetails.value || downloading.value) return
  downloading.value = true
  try {
    const durationSeconds = mixDetails.value.durationSeconds ?? 0
    if (durationSeconds <= 0) return
    const blob = await audioEngine.renderOffline(mixDetails.value, durationSeconds, STATIC_BASE_URL)
    const suggestedName = `${buildExportBaseName()}.wav`

    const showPicker = (window as any).showSaveFilePicker
    if (typeof showPicker === 'function') {
      try {
        const handle = await showPicker({
          suggestedName,
          types: [{ description: 'WAV file', accept: { 'audio/wav': ['.wav'] } }],
        })
        const writable = await handle.createWritable()
        await writable.write(blob)
        await writable.close()
        return
      } catch (e: any) {
        if (e?.name === 'AbortError') return
        console.error('showSaveFilePicker failed, falling back to direct download:', e)
      }
    }

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = suggestedName
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Failed to render mix:', error)
  } finally {
    downloading.value = false
  }
}

const handleRewind = () => {
  mixStore.seek(0)
  const container = document.getElementById('tracks-container')
  if (container) {
    container.scrollLeft = 0
    container.scrollTop = 0
  }
}

const handleZoom = (direction: 'in' | 'out') => {
  const container = document.getElementById('tracks-container')
  const cursorTime = currentTime.value
  const oldPps = pixelsPerSecond.value

  if (direction === 'in') mixStore.zoomIn()
  else mixStore.zoomOut()

  const newPps = pixelsPerSecond.value
  if (oldPps === newPps || !container) return

  // Adjust scroll so cursor position stays visually stationary
  if (isHorizontal.value) {
    const cursorViewport = cursorTime * oldPps - container.scrollLeft
    container.scrollLeft = cursorTime * newPps - cursorViewport
  } else {
    const cursorViewport = cursorTime * oldPps - container.scrollTop
    container.scrollTop = cursorTime * newPps - cursorViewport
  }
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 100)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`
}

const handleAddTrack = async () => {
  if (!newTrackName.value) return
  addingTrack.value = true
  try {
    await mixStore.addTrack(newTrackName.value)
    newTrackName.value = ''
  } finally {
    addingTrack.value = false
  }
}

const triggerFileUpload = (trackId: number) => {
  const event = new CustomEvent('trigger-upload', { detail: { trackId, time: currentTime.value } })
  window.dispatchEvent(event)
}

// Source picker for the track "+" button: lets the user pick a clip from
// their personal library OR upload a file fresh.
const showAddClipSourceDialog = ref(false)
const showLibraryPickerDialog = ref(false)
const addClipSourceTrackId = ref<number | null>(null)
const libraryFetchOnce = ref(false)
const addingFromLibrary = ref(false)

const openAddClipDialog = (trackId: number) => {
  addClipSourceTrackId.value = trackId
  showAddClipSourceDialog.value = true
}

const handlePickSourceUpload = () => {
  showAddClipSourceDialog.value = false
  if (addClipSourceTrackId.value != null) triggerFileUpload(addClipSourceTrackId.value)
}

const userLibraryStore = useUserLibraryStore()

const handlePickSourceLibrary = async () => {
  showAddClipSourceDialog.value = false
  showLibraryPickerDialog.value = true
  if (!libraryFetchOnce.value) {
    await userLibraryStore.fetch()
    libraryFetchOnce.value = true
  }
}

const handleAddFromLibrary = async (libraryClipId: number) => {
  if (addClipSourceTrackId.value == null) return
  addingFromLibrary.value = true
  try {
    const newClip = await userLibraryStore.addToTrack(addClipSourceTrackId.value, libraryClipId, currentTime.value)
    if (newClip && mixStore.currentMix) {
      const t = mixStore.currentMix.tracks.find(tr => tr.id === addClipSourceTrackId.value)
      if (t) {
        t.clips.push(newClip)
        await audioEngine.loadClip(newClip, STATIC_BASE_URL)
      }
    }
    showLibraryPickerDialog.value = false
  } catch (e: any) {
    uiStore.showToast('error', 'Library', e?.response?.data?.error ?? e?.message ?? 'Failed to add clip from library.')
  } finally {
    addingFromLibrary.value = false
  }
}

// AI Generate
const showGenerateDialog = ref(false)
const generateTrackId = ref<number | null>(null)
const generatePrompt = ref('')
const generateEngine = ref<'drums' | 'piano' | 'bass' | 'dev'>('drums')

// AI Dev experimental state
interface DevClassifyPane {
  label: string
  responseText: string
  inputTokens: number
  outputTokens: number
  aiModel: string
  cost: number | null
}
const devMidiFile = ref<File | null>(null)
const devParsedMidi = ref<import('../../utils/midi').ParsedMidi | null>(null)
const devClassifyResults = ref<DevClassifyPane[]>([])
const devCompareWithProfile = ref(false)
const devCompareWithLocal = ref(false)
const devClassifying = ref(false)
const devRendering = ref(false)
const devError = ref('')

// AI Dev — render pasted MIDI JSON
const showMidiJsonDialog = ref(false)
const devMidiJsonText = ref('')
const devMidiJsonRendering = ref(false)
const devMidiJsonError = ref('')

// AI Dev — render pasted step-grid
const showStepGridDialog = ref(false)
const devStepGridText = ref('')
const devStepGridBars = ref(4)
const devStepGridRendering = ref(false)
const devStepGridError = ref('')

const openMidiJsonDialog = () => {
  devMidiJsonText.value = ''
  devMidiJsonError.value = ''
  showMidiJsonDialog.value = true
}

const computeJsonRenderDuration = (parsed: any): number => {
  if (!parsed?.notes || !Array.isArray(parsed.notes) || parsed.notes.length === 0) return 4
  let maxEnd = 0
  for (const n of parsed.notes) {
    if (!Array.isArray(n) || n.length < 2) continue
    const time = Number(n[1]) || 0
    // 4-element [pitch, time, duration, velocity]; 3-element [pitch, time, velocity]
    const dur = n.length >= 4 ? Number(n[2]) || 0 : 0.2
    const end = time + dur
    if (end > maxEnd) maxEnd = end
  }
  return Math.max(4, Math.ceil(maxEnd) + 1)
}

const handleRenderMidiJson = async () => {
  const raw = devMidiJsonText.value.trim()
  if (!raw || generateTrackId.value === null) return
  devMidiJsonError.value = ''

  let parsed: any
  try { parsed = JSON.parse(raw) }
  catch (e: any) {
    devMidiJsonError.value = `Invalid JSON: ${e.message}`
    return
  }

  const bpm = parsed.bpm || mixStore.currentMix?.bpm || 120
  const duration = computeJsonRenderDuration(parsed)
  const filename = `pasted-midi-${Date.now()}.wav`

  devMidiJsonRendering.value = true
  try {
    await mixStore.renderMidiJson(generateTrackId.value, currentTime.value, {
      midiJson: raw,
      instrument: 'drums',
      bpm,
      duration,
    }, filename)
    showMidiJsonDialog.value = false
  } catch (e: any) {
    devMidiJsonError.value = `Render failed: ${e.response?.data?.error ?? e.message ?? e}`
  } finally {
    devMidiJsonRendering.value = false
  }
}

const openStepGridDialog = () => {
  devStepGridText.value = ''
  devStepGridError.value = ''
  showStepGridDialog.value = true
}

const handleRenderStepGrid = async () => {
  const raw = devStepGridText.value.trim()
  if (!raw || generateTrackId.value === null) return
  devStepGridError.value = ''

  const bpm = mixStore.currentMix?.bpm || 120
  const ts = mixStore.currentMix?.timeSignature || '4/4'
  const filename = `pasted-stepgrid-${Date.now()}.wav`

  devStepGridRendering.value = true
  try {
    await mixStore.renderStepGrid(generateTrackId.value, currentTime.value, {
      gridText: raw,
      bars: devStepGridBars.value || 4,
      bpm,
      timeSignature: ts,
      instrument: 'drums',
    }, filename)
    showStepGridDialog.value = false
  } catch (e: any) {
    devStepGridError.value = `Render failed: ${e.response?.data?.error ?? e.message ?? e}`
  } finally {
    devStepGridRendering.value = false
  }
}

// AI Dev — drum prompt preview
const devTestPrompt = ref('')
const devTestBars = ref(4)
const devTestUseLibrary = ref(true)
const devTestFormat = ref<'json' | 'grid'>('json')
const devCompareRunning = ref(false)
const devCompareResult = ref<any>(null)
const showCompareDialog = ref(false)
const devTestPromptResult = ref<{
  promptText: string
  resolvedFolderPrefix?: string | null
  retrievedLoops: Array<{ filename: string, genre: string, sectionHint: string, feel: string, backbeatType: string, density: string, bpm: number, score: number, matchReason: string }>
} | null>(null)
const devTestingPrompt = ref(false)
const showTestPromptDialog = ref(false)

const devPickFile = async () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.mid,.midi'
  input.onchange = async () => {
    const f = input.files?.[0]
    if (!f) return
    devMidiFile.value = f
    devClassifyResults.value = []
    devError.value = ''
    try {
      const { parseMidiFile } = await import('../../utils/midi')
      devParsedMidi.value = await parseMidiFile(f)
    } catch (e: any) {
      devError.value = `Failed to parse MIDI: ${e.message ?? e}`
      devParsedMidi.value = null
    }
  }
  input.click()
}

const devClassify = async () => {
  if (!devParsedMidi.value) return
  devClassifying.value = true
  devError.value = ''
  devClassifyResults.value = []

  const basePayload = {
    filename: devParsedMidi.value.filename,
    bpm: devParsedMidi.value.bpm,
    timeSignature: devParsedMidi.value.timeSignature,
    bars: devParsedMidi.value.bars,
    noteCount: devParsedMidi.value.noteCount,
    gridText: devParsedMidi.value.gridText,
  }

  const calls: Promise<DevClassifyPane>[] = [
    mixStore.classifyMidi({ ...basePayload, useProfile: false }).then(r => ({
      label: 'Fixed (Standard, no thinking)',
      ...r,
      responseText: prettyJson(r.responseText),
    }))
  ]
  if (devCompareWithProfile.value) {
    calls.push(
      mixStore.classifyMidi({ ...basePayload, useProfile: true }).then(r => ({
        label: 'Profile model',
        ...r,
        responseText: prettyJson(r.responseText),
      }))
    )
  }
  if (devCompareWithLocal.value) {
    calls.push(
      mixStore.classifyMidi({ ...basePayload, useLocal: true }).then(r => ({
        label: 'Local AI',
        ...r,
        responseText: prettyJson(r.responseText),
      }))
    )
  }

  try {
    devClassifyResults.value = await Promise.all(calls)
  } catch (e: any) {
    devError.value = `Classification failed: ${e.response?.data?.error ?? e.message ?? e}`
  } finally {
    devClassifying.value = false
  }
}

const prettyJson = (s: string) => {
  try { return JSON.stringify(JSON.parse(s), null, 2) } catch { return s }
}

const devTestPromptHandler = async () => {
  if (!devTestPrompt.value.trim()) return
  devTestingPrompt.value = true
  devError.value = ''
  try {
    devTestPromptResult.value = await mixStore.previewDrumPrompt({
      prompt: devTestPrompt.value.trim(),
      bars: devTestBars.value || 4,
      bpm: mixStore.currentMix?.bpm || 120,
      timeSignature: mixStore.currentMix?.timeSignature || '4/4',
      useLibraryReference: devTestUseLibrary.value,
      format: devTestFormat.value,
    })
    showTestPromptDialog.value = true
  } catch (e: any) {
    devError.value = `Test prompt failed: ${e.response?.data?.error ?? e.message ?? e}`
  } finally {
    devTestingPrompt.value = false
  }
}

const devRenderCompareHandler = async () => {
  if (!devTestPrompt.value.trim()) return
  devCompareRunning.value = true
  devError.value = ''
  try {
    devCompareResult.value = await mixStore.renderDrumsCompare({
      prompt: devTestPrompt.value.trim(),
      bars: devTestBars.value || 4,
      bpm: mixStore.currentMix?.bpm || 120,
      timeSignature: mixStore.currentMix?.timeSignature || '4/4',
      useLibraryReference: devTestUseLibrary.value,
    })
    showCompareDialog.value = true
  } catch (e: any) {
    devError.value = `Render compare failed: ${e.response?.data?.error ?? e.message ?? e}`
  } finally {
    devCompareRunning.value = false
  }
}

// Builds an audio src URL from a base64 WAV blob returned by the compare endpoint.
function compareAudioSrc(b64: string): string {
  if (!b64) return ''
  return `data:audio/wav;base64,${b64}`
}

const devRender = async () => {
  if (!devParsedMidi.value || generateTrackId.value === null) return
  devRendering.value = true
  devError.value = ''
  try {
    await mixStore.renderMidi(generateTrackId.value, currentTime.value, {
      bpm: devParsedMidi.value.bpm,
      duration: devParsedMidi.value.durationSeconds,
      instrument: 'drums',
      notes: devParsedMidi.value.notes,
    }, devParsedMidi.value.filename)
    showGenerateDialog.value = false
  } catch (e: any) {
    devError.value = `Render failed: ${e.response?.data?.error ?? e.message ?? e}`
  } finally {
    devRendering.value = false
  }
}
const generateBars = ref(4)
const useDrumLibraryReference = ref(true)
const useDrumReference = ref(false)
const drumReferenceClipId = ref<number | null>(null)
const usePianoReference = ref(false)
const pianoReferenceClipId = ref<number | null>(null)
const useBassReference = ref(false)
const bassReferenceClipId = ref<number | null>(null)

const drumPromptExamples = [
  'Hard rock beat straight no transitions just kick snare and high-hat',
  'Funky breakbeat with syncopated hi-hats and ghost notes on snare',
  'Slow blues shuffle with ride cymbal and kick on 1 and 3',
  'Latin bossa nova pattern with cross-stick snare',
  'Punk rock fast 8th note hi-hat with driving kick and snare on 2 and 4',
  'Jazz swing ride pattern with kick comping and brush snare',
  'Reggae one drop with rimshot snare on beat 3',
  'Half-time groove with open hi-hat accents',
]

const pianoPromptExamples = [
  'Jazzy Rhodes comping in C minor with lush 7th chords and syncopated rhythm',
  'Simple pop ballad chords in G major, whole notes and half notes',
  'Funky clavinet-style stabs in E minor with 16th note rhythms',
  'Classical arpeggiated pattern in A minor, flowing eighth notes',
  'Blues piano shuffle in Bb with left hand walking bass',
  'Ambient pad-like sustained chords in D major, sparse and dreamy',
  'Latin montuno pattern in F minor with syncopated octaves',
  'Rock power chord riff in E with driving eighth notes',
]

const bassPromptExamples = [
  'Driving rock bass line in E, root notes on the downbeats with octave jumps',
  'Funky slap bass in A minor with syncopated 16ths and ghost notes',
  'Walking jazz bass in F major, quarter notes with chromatic passing tones',
  'Reggae bass line in G, root-fifth pattern with space and dub feel',
  'Motown bass in C major, melodic with passing notes and syncopation',
  'Metal gallop in drop D, fast palm-muted 16th notes on low D',
  'Disco bass in D minor, pumping 8th notes with occasional octave jumps',
  'Hip hop bass in F minor, sub-heavy with sustained root notes and slides',
]

const openGenerateDialog = (trackId: number) => {
  generateTrackId.value = trackId
  generatePrompt.value = ''
  useDrumLibraryReference.value = true
  useDrumReference.value = false
  drumReferenceClipId.value = null
  usePianoReference.value = false
  pianoReferenceClipId.value = null
  useBassReference.value = false
  bassReferenceClipId.value = null
  devMidiFile.value = null
  devParsedMidi.value = null
  devClassifyResults.value = []
  devCompareWithProfile.value = false
  devCompareWithLocal.value = false
  devTestPromptResult.value = null
  devError.value = ''
  mixStore.generateError = ''
  showGenerateDialog.value = true
}

const handleGenerate = async () => {
  if (generateTrackId.value === null) return
  if (!generatePrompt.value.trim()) return

  showGenerateDialog.value = false
  try {
    if (generateEngine.value === 'drums') {
      await mixStore.generateDrums(
        generateTrackId.value,
        generatePrompt.value.trim(),
        currentTime.value,
        generateBars.value,
        mixStore.currentMix?.bpm || undefined,
        mixStore.currentMix?.timeSignature || undefined,
        useDrumReference.value && drumReferenceClipId.value ? drumReferenceClipId.value : undefined,
        useDrumLibraryReference.value
      )
    } else if (generateEngine.value === 'bass') {
      await mixStore.generateBass(
        generateTrackId.value,
        generatePrompt.value.trim(),
        currentTime.value,
        generateBars.value,
        mixStore.currentMix?.bpm || undefined,
        mixStore.currentMix?.timeSignature || undefined,
        useBassReference.value && bassReferenceClipId.value ? bassReferenceClipId.value : undefined
      )
    } else {
      await mixStore.generatePiano(
        generateTrackId.value,
        generatePrompt.value.trim(),
        currentTime.value,
        generateBars.value,
        mixStore.currentMix?.bpm || undefined,
        mixStore.currentMix?.timeSignature || undefined,
        usePianoReference.value && pianoReferenceClipId.value ? pianoReferenceClipId.value : undefined
      )
    }
  } catch {
    showGenerateDialog.value = true
  }
}

const handleTimelineClick = (e: MouseEvent) => {
  const container = document.getElementById('tracks-container')
  if (!container) return
  const rect = container.getBoundingClientRect()

  if (isHorizontal.value) {
    const x = e.clientX - rect.left + container.scrollLeft
    mixStore.seek(Math.max(0, x / pixelsPerSecond.value))
  } else {
    const y = e.clientY - rect.top + container.scrollTop
    mixStore.seek(Math.max(0, y / pixelsPerSecond.value))
  }
}

const handleWheel = (e: WheelEvent) => {
  if (!isHorizontal.value) return
  const container = document.getElementById('tracks-container')
  if (!container) return
  e.preventDefault()

  if (e.ctrlKey || e.metaKey) {
    // Ctrl+wheel = zoom centered on hover position
    const anchorTime = hoverTime.value ?? currentTime.value
    const oldPps = pixelsPerSecond.value

    if (e.deltaY < 0) mixStore.zoomIn()
    else mixStore.zoomOut()

    const newPps = pixelsPerSecond.value
    if (oldPps !== newPps) {
      if (isHorizontal.value) {
        const anchorViewport = anchorTime * oldPps - container.scrollLeft
        container.scrollLeft = anchorTime * newPps - anchorViewport
      } else {
        const anchorViewport = anchorTime * oldPps - container.scrollTop
        container.scrollTop = anchorTime * newPps - anchorViewport
      }
    }
  } else {
    container.scrollLeft += e.deltaY || e.deltaX
  }
}

const handleHeadersWheel = (e: WheelEvent) => {
  if (!isHorizontal.value) return
  const container = document.getElementById('tracks-container')
  if (!container) return
  container.scrollTop += e.deltaY
}

const handleScroll = (e: Event) => {
  const target = e.target as HTMLElement
  const headers = document.getElementById('headers-container')

  scrollLeft.value = target.scrollLeft
  scrollTop.value = target.scrollTop

  if (isHorizontal.value) {
    if (headers) headers.scrollTop = target.scrollTop
  } else {
    if (headers) headers.scrollLeft = target.scrollLeft
  }

  if (hoverTime.value !== null) {
    if (isHorizontal.value) {
      hoverPos.value = lastMousePos.value + target.scrollLeft
    } else {
      hoverPos.value = lastMousePos.value + target.scrollTop
    }
    hoverTime.value = Math.max(0, hoverPos.value / pixelsPerSecond.value)
  }
}
</script>

<template>
  <div class="flex flex-col h-[calc(100vh-8rem)] lg:h-[calc(100vh-12rem)] space-y-2 lg:space-y-3">
    <!-- Row 1: Back + Song Title + Mix Name -->
    <div class="flex items-center gap-4 shrink-0 mt-0 lg:mt-0 lg:mb-4 mb-0">
      <Button icon="pi pi-arrow-left" text rounded severity="secondary" @click="handleBack" />
      <div class="lg:block hidden">
        <p class="rd-label leading-none mb-1">{{ song?.title }}</p>
        <h1 class="text-2xl font-black text-slate-900 leading-tight">{{ mix.title }}</h1>
      </div>
      <h1 class="lg:hidden text-sm text-slate-900 leading-tight truncate">{{ song?.title }} / <span class="font-bold">{{ mix.title }}</span></h1>
      <Button v-if="bandStore.canEditMixes" icon="pi pi-pencil" text rounded severity="secondary" size="small" class="!w-8 !h-8 shrink-0" v-tooltip.bottom="'Edit mix info'" @click="openMixInfo" />
    </div>

    <!-- Mobile/Tablet: 3 separate rows (hidden on lg) -->
    <div class="lg:hidden space-y-2 shrink-0">
      <!-- Row: Toolbar -->
      <div class="flex items-center justify-center gap-3 bg-white p-2 px-3 rounded-2xl border border-slate-200 shadow-sm">
        <Button icon="pi pi-sliders-h" :severity="mixerVisible ? 'info' : 'secondary'" :text="!mixerVisible" rounded size="small" class="w-8 h-8" v-tooltip.bottom="mixerVisible ? 'Hide mixer' : 'Show mixer'" @click="mixerVisible = !mixerVisible" />
        <Button icon="pi pi-stopwatch" :severity="metronomeOn ? 'warn' : 'secondary'" :text="!metronomeOn" rounded size="small" class="w-8 h-8" v-tooltip.bottom="metronomeOn ? `Metronome on (${mixStore.metronomeBpm} BPM)` : 'Metronome off'" @click="mixStore.toggleMetronome()" />
        <Button :icon="downloading ? 'pi pi-spin pi-spinner' : 'pi pi-download'" severity="secondary" text rounded size="small" class="w-8 h-8" :disabled="downloading || isPlaying || isRecording" v-tooltip.bottom="'Download mix'" @click="handleDownloadMix" />
      </div>

      <!-- Row: Transport -->
      <div class="flex items-center justify-center bg-white p-2 px-3 rounded-2xl border border-slate-200 shadow-sm">
        <div class="flex items-center gap-2">
          <div class="flex items-center gap-1">
            <Button icon="pi pi-step-backward" severity="secondary" text rounded size="small" class="w-8 h-8" @click="handleRewind" />
            <Button :icon="isPlaying ? 'pi pi-pause' : 'pi pi-play'" :severity="isPlaying ? 'success' : 'primary'" rounded size="small" class="w-8 h-8" :disabled="isRecording" @click="togglePlay" />
            <button
              v-if="bandStore.canEditMixes"
              class="w-8 h-8 rounded-full flex items-center justify-center transition-all border-2"
              :class="isRecording ? 'bg-red-500 border-red-600 shadow-lg shadow-red-500/30' : armedTrackId !== null ? 'bg-red-100 border-red-300 hover:bg-red-200' : 'bg-slate-100 border-slate-200 opacity-50 cursor-not-allowed'"
              :disabled="armedTrackId === null" @click="toggleRecord"
              v-tooltip.bottom="isRecording ? 'Stop recording' : armedTrackId !== null ? 'Record' : 'Arm a track first'"
            >
              <span class="block rounded-full transition-all" :class="isRecording ? 'w-3 h-3 bg-white rounded-sm' : 'w-3.5 h-3.5 bg-red-500'"></span>
            </button>
            <Button icon="pi pi-expand" :severity="snapToBars ? 'info' : 'secondary'" :text="!snapToBars" rounded size="small" class="w-8 h-8" v-tooltip.bottom="snapToBars ? `Snap to bars on (${mixStore.metronomeBpm} BPM, ${mixStore.metronomeBeatsPerBar}/4)` : 'Snap to bars off'" @click="snapToBars = !snapToBars" />
            <Button v-if="bandStore.canEditMixes" icon="pi pi-minus" :severity="razorMode ? 'danger' : 'secondary'" :text="!razorMode" rounded size="small"
                    class="w-8 h-8 -rotate-[60deg]" :class="razorMode ? '!bg-red-50 !border-red-200' : ''"
                    v-tooltip.bottom="razorMode ? 'Exit razor mode' : 'Razor: split clips'"
                    @click="razorMode = !razorMode" />
          </div>
        </div>
      </div>

      <!-- Row: Zoom + Add Track -->
      <div class="flex items-center gap-2 bg-white p-2 px-3 rounded-2xl border border-slate-200 shadow-sm">
        <div class="flex items-center gap-1 shrink-0">
          <Button icon="pi pi-minus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="handleZoom('out')" v-tooltip.bottom="'Zoom out'" />
          <i class="pi pi-search text-slate-400 text-xs"></i>
          <Button icon="pi pi-plus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="handleZoom('in')" v-tooltip.bottom="'Zoom in'" />
        </div>
        <template v-if="bandStore.canEditMixes">
          <div class="h-6 w-px bg-slate-200"></div>
          <div class="flex items-center gap-2 flex-1 min-w-0">
            <InputText v-model="newTrackName" placeholder="New track name..." size="small" class="flex-1 min-w-0 text-xs h-8 border-none bg-transparent focus:shadow-none" @keyup.enter="handleAddTrack" />
            <Button icon="pi pi-plus" size="small" class="!h-7 !w-7 !p-0 shrink-0" :loading="addingTrack" @click="handleAddTrack" />
          </div>
        </template>
      </div>
    </div>

    <!-- Desktop: Single toolbar row (hidden below lg) -->
    <div class="hidden lg:flex items-center gap-3 bg-white p-2 px-3 rounded-2xl border border-slate-200 shadow-sm shrink-0">
      <!-- Transport -->
      <div class="flex items-center gap-2">
        <div class="bg-slate-50 px-3 py-1 rounded-xl border border-slate-100 min-w-[85px] text-center">
          <p class="text-[14px] font-mono font-bold text-slate-700 leading-none">{{ formatTime(currentTime) }}</p>
        </div>
        <div class="flex items-center gap-1">
          <Button icon="pi pi-step-backward" severity="secondary" text rounded size="small" class="w-8 h-8" @click="handleRewind" />
          <Button :icon="isPlaying ? 'pi pi-pause' : 'pi pi-play'" :severity="isPlaying ? 'success' : 'primary'" rounded size="small" class="w-8 h-8" :disabled="isRecording" @click="togglePlay" />
          <Button icon="pi pi-stopwatch" :severity="metronomeOn ? 'warn' : 'secondary'" :text="!metronomeOn" rounded size="small" class="w-8 h-8" v-tooltip.bottom="metronomeOn ? `Metronome on (${mixStore.metronomeBpm} BPM)` : 'Metronome off'" @click="mixStore.toggleMetronome()" />
          <button
            v-if="bandStore.canEditMixes"
            class="w-8 h-8 rounded-full flex items-center justify-center transition-all border-2"
            :class="isRecording ? 'bg-red-500 border-red-600 shadow-lg shadow-red-500/30' : armedTrackId !== null ? 'bg-red-100 border-red-300 hover:bg-red-200' : 'bg-slate-100 border-slate-200 opacity-50 cursor-not-allowed'"
            :disabled="armedTrackId === null" @click="toggleRecord"
            v-tooltip.bottom="isRecording ? 'Stop recording' : armedTrackId !== null ? 'Record' : 'Arm a track first'"
          >
            <span class="block rounded-full transition-all" :class="isRecording ? 'w-3 h-3 bg-white rounded-sm' : 'w-3.5 h-3.5 bg-red-500'"></span>
          </button>
          <Button icon="pi pi-expand" :severity="snapToBars ? 'info' : 'secondary'" :text="!snapToBars" rounded size="small" class="w-8 h-8" v-tooltip.bottom="snapToBars ? `Snap to bars on (${mixStore.metronomeBpm} BPM, ${mixStore.metronomeBeatsPerBar}/4)` : 'Snap to bars off'" @click="snapToBars = !snapToBars" />
          <Button :icon="downloading ? 'pi pi-spin pi-spinner' : 'pi pi-download'" severity="secondary" text rounded size="small" class="w-8 h-8" :disabled="downloading || isPlaying || isRecording" v-tooltip.bottom="'Download mix'" @click="handleDownloadMix" />
          <Button v-if="bandStore.canEditMixes" icon="pi pi-minus" :severity="razorMode ? 'danger' : 'secondary'" :text="!razorMode" rounded size="small"
                  class="w-8 h-8 -rotate-[60deg]" :class="razorMode ? '!bg-red-50 !border-red-200' : ''"
                  v-tooltip.bottom="razorMode ? 'Exit razor mode' : 'Razor: split clips'"
                  @click="razorMode = !razorMode" />
        </div>
      </div>

      <div class="h-6 w-px bg-slate-200"></div>

      <!-- Zoom -->
      <div class="flex items-center gap-1 shrink-0">
        <Button icon="pi pi-minus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="handleZoom('out')" v-tooltip.bottom="'Zoom out'" />
        <i class="pi pi-search text-slate-400 text-xs"></i>
        <Button icon="pi pi-plus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="handleZoom('in')" v-tooltip.bottom="'Zoom in'" />
      </div>

      <template v-if="bandStore.canEditMixes">
        <div class="h-6 w-px bg-slate-200"></div>

        <!-- Add Track -->
        <div class="flex items-center gap-2 w-48">
          <InputText v-model="newTrackName" placeholder="New track..." size="small" class="flex-1 text-xs h-8 border-none bg-transparent focus:shadow-none" @keyup.enter="handleAddTrack" />
          <Button icon="pi pi-plus" size="small" class="!h-7 !w-7 !p-0 shrink-0" :loading="addingTrack" @click="handleAddTrack" />
        </div>
      </template>

      <span class="rd-label text-slate-300 ml-auto">{{ mixDetails?.tracks.length || 0 }} Tracks</span>
    </div>

    <!-- Main Workspace -->
    <div class="flex-1 rd-card-inner overflow-hidden flex flex-col relative">
      <!-- Loading Overlay -->
      <div v-if="mixStore.loading" class="absolute inset-0 z-[100] bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center">
        <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" fill="transparent" animationDuration=".5s" />
        <p class="mt-4 text-slate-500 font-medium animate-pulse">Loading mix & audio...</p>
      </div>

      <div v-if="mixStore.uploading" class="absolute inset-0 z-[100] bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center">
        <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" fill="transparent" animationDuration=".5s" />
        <p class="mt-4 text-slate-500 font-medium animate-pulse">Uploading audio...</p>
      </div>

      <div v-if="mixStore.generating" class="absolute inset-0 z-[100] bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center">
        <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" fill="transparent" animationDuration=".5s" />
        <p class="mt-4 text-slate-500 font-medium animate-pulse">Generating audio with AI...</p>
      </div>

      <!-- TIMELINE VIEW - Horizontal (Desktop lg+) -->
      <div v-if="isHorizontal" class="flex-1 flex overflow-hidden">

        <!-- Track Headers (Fixed left column) -->
        <div class="w-52 bg-slate-100 border-r border-slate-200 flex flex-col shrink-0 overflow-hidden" id="headers-container"
             @wheel.prevent="handleHeadersWheel">
          <div v-for="track in mixDetails?.tracks" :key="track.id"
               class="h-24 flex border-b border-slate-300 shrink-0">
            <div class="flex-1 min-w-0 flex flex-col justify-between px-2 py-1.5">
            <!-- Row 1: Buttons -->
            <div class="flex items-center gap-1.5">
              <template v-if="bandStore.canEditMixes">
                <i
                  class="pi pi-plus text-slate-400 hover:text-teal-500 transition-colors cursor-pointer"
                  style="font-size: 0.8rem"
                  v-tooltip.top="'Add clip at cursor'"
                  @click.stop="openAddClipDialog(track.id)"
                ></i>
                <i
                  class="pi pi-sparkles text-slate-400 hover:text-teal-500 transition-colors cursor-pointer"
                  style="font-size: 0.8rem"
                  v-tooltip.top="'Generate with AI'"
                  @click.stop="openGenerateDialog(track.id)"
                ></i>
                <button
                  class="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black transition-all"
                  :class="armedTrackId === track.id
                    ? 'bg-red-500 text-white shadow-sm shadow-red-500/40'
                    : 'bg-slate-200 text-slate-400 hover:bg-red-100 hover:text-red-400'"
                  :disabled="isRecording"
                  v-tooltip.top="armedTrackId === track.id ? 'Disarm track' : 'Arm for recording'"
                  @click.stop="mixStore.armTrack(track.id)"
                >R</button>
              </template>
              <button
                class="text-[9px] font-black w-5 h-5 rounded-md transition-all border flex items-center justify-center"
                :class="track.isMuted ? 'bg-teal-500 text-white border-teal-600' : 'bg-slate-50 text-slate-400 border-slate-300 hover:bg-slate-100'"
                v-tooltip.top="'Mute'"
                @click.stop="mixStore.updateTrack(track.id, { isMuted: !track.isMuted })"
              >M</button>
              <button
                class="text-[9px] font-black w-5 h-5 rounded-md transition-all border flex items-center justify-center"
                :class="track.isSolo ? 'bg-teal-500 text-white border-teal-600' : 'bg-slate-50 text-slate-400 border-slate-300 hover:bg-slate-100'"
                v-tooltip.top="'Solo'"
                @click.stop="mixStore.updateTrack(track.id, { isSolo: !track.isSolo })"
              >S</button>
              <i
                class="pi pi-download text-slate-400 hover:text-sky-500 transition-colors cursor-pointer ml-auto"
                :class="{ 'animate-pulse !text-sky-400': downloadingTrackId === track.id }"
                style="font-size: 0.8rem"
                v-tooltip.top="'Download track'"
                @click.stop="handleDownloadTrack(track)"
              ></i>
              <i
                v-if="bandStore.canEditMixes"
                class="pi pi-trash text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                style="font-size: 0.8rem"
                v-tooltip.top="'Delete track'"
                @click.stop="confirmDeleteTrack(track)"
              ></i>
            </div>
            <!-- Row 2: Track Name -->
            <span class="text-[11px] font-semibold text-slate-700 uppercase tracking-tight truncate cursor-pointer hover:text-teal-600 transition-colors px-0.5"
                  @click.stop="openRenameTrack(track.id, track.name)">
              {{ track.name }}
            </span>
            <!-- Row 3: Pan -->
            <div class="flex items-center gap-1 mt-1">
              <span class="text-[8px] text-slate-400 font-bold">L</span>
              <input type="range" :value="track.pan * 100" min="-100" max="100"
                class="flex-1 h-0.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
                @input="($event: any) => { let v = Number($event.target.value); if (Math.abs(v) <= 10) v = 0; track.pan = v / 100; $event.target.value = v; audioEngine.updateTrackPan(track.id, track.pan) }"
                @change="() => mixStore.updateTrack(track.id, { pan: track.pan })"
              />
              <span class="text-[8px] text-slate-400 font-bold">R</span>
            </div>
            <!-- Row 4: Volume -->
            <div class="flex items-center gap-1">
              <span class="text-[9px] font-bold text-teal-600 w-7">{{ Math.round(track.volume * 100) }}%</span>
              <input type="range" :value="track.volume * 100" min="0" max="150" step="1"
                class="flex-1 h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
                @input="($event: any) => { let v = Number($event.target.value); if (Math.abs(v - 100) <= 10) v = 100; track.volume = v / 100; $event.target.value = v; const anySolo = mixDetails?.tracks.some(t => t.isSolo) ?? false; const silenced = track.isMuted || (anySolo && !track.isSolo); audioEngine.updateTrackVolume(track.id, track.volume, silenced) }"
                @change="() => mixStore.updateTrack(track.id, { volume: track.volume })"
              />
            </div>
            </div>
            <!-- Real-time level meter on the border with the timeline -->
            <div class="w-2 shrink-0">
              <TrackMeter :track-id="track.id" />
            </div>
          </div>
        </div>

        <!-- Scrollable Tracks Area -->
        <div class="flex-1 overflow-auto relative flex flex-col" id="tracks-container"
             @scroll="handleScroll"
             @wheel.prevent="handleWheel"
             @mousemove="handleMouseMoveTimeline"
             @mouseleave="handleMouseLeaveTimeline">

          <!-- Hover Guide Line (vertical, spans full track stack) -->
          <div v-if="hoverTime !== null"
               class="absolute w-px bg-slate-300 z-30 pointer-events-none"
               :style="hoverLineStyle"></div>
          <!-- Hover Timecode Label (kept centered in viewport) -->
          <div v-if="hoverTime !== null"
               class="absolute -translate-x-1/2 -translate-y-1/2 bg-white/90 text-slate-400 text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-100 shadow-sm whitespace-nowrap z-30 pointer-events-none"
               :style="hoverLabelStyle">
            {{ formatTime(hoverTime) }}
          </div>

          <!-- Playback Cursor (vertical line, spans full track stack) -->
          <div class="absolute w-[2px] bg-teal-500 z-40 pointer-events-none"
               :style="cursorLineStyle"></div>
          <!-- Cursor Timecode Label (kept centered in viewport) -->
          <div class="absolute -translate-x-1/2 -translate-y-1/2 bg-slate-900 text-white text-[11px] font-mono px-2.5 py-1 rounded-lg shadow-2xl whitespace-nowrap pointer-events-none border border-slate-700 z-50"
               :style="cursorLabelStyle">
            {{ formatTime(currentTime) }}
          </div>
          <!-- Draggable Triangle (kept at top of viewport) -->
          <div
            class="absolute w-3 h-4 bg-teal-500 -translate-x-[5px] rounded-b-sm shadow-md pointer-events-auto cursor-grab active:cursor-grabbing z-50"
            style="clip-path: polygon(0 0, 100% 0, 50% 100%)"
            :style="cursorHandleStyle"
            @mousedown="handleMouseDownCursor"
          ></div>

          <!-- Empty state placeholder -->
          <div v-if="!mixDetails?.tracks.length" class="absolute inset-0 flex flex-col items-center justify-center text-slate-300">
            <i class="pi pi-list text-5xl mb-4 opacity-20"></i>
            <p class="text-sm italic">No tracks yet.</p>
          </div>

          <!-- Always-on seek layer: lets clicks below/between tracks move the cursor too. -->
          <div class="absolute inset-0 z-10 cursor-pointer" @click="handleTimelineClick"></div>

          <!-- Track rows (z-20 so they sit above the seek layer and keep their own click handling) -->
          <TimelineTrack
            v-for="track in mixDetails?.tracks"
            :key="track.id"
            :track="track"
            :mix-duration-seconds="mixDetails?.durationSeconds || 300"
            :horizontal="true"
            :readonly="!bandStore.canEditMixes"
            class="relative z-20"
          />
        </div>
      </div>

      <!-- TIMELINE VIEW - Vertical (Mobile/Tablet) -->
      <div v-if="!isHorizontal" class="flex-1 flex flex-col overflow-hidden min-h-0">

        <!-- Track Headers (Fixed at top) -->
        <div class="h-[7.825rem] bg-slate-100 border-b border-slate-200 flex shrink-0 overflow-hidden">
          <div class="flex-1 flex overflow-x-hidden" id="headers-container">
            <div v-for="track in mixDetails?.tracks" :key="track.id"
                 class="w-16 h-full flex flex-col border-r border-slate-300 shrink-0"
                 @click.stop>
              <div class="flex-1 min-h-0 flex">
              <!-- Left column: Buttons (2/3) -->
              <div class="w-2/3 h-full flex flex-col items-center gap-2 pt-[10px]">
                <Button
                  v-if="bandStore.canEditMixes"
                  icon="pi pi-trash"
                  text
                  rounded
                  class="!w-6 !h-6 !p-0 text-slate-300 hover:!text-red-500 transition-colors text-[12px]"
                  style="color: #cbd5e1"
                  v-tooltip.right="'Delete track'"
                  @click.stop="confirmDeleteTrack(track)"
                />
                <button
                  v-if="bandStore.canEditMixes"
                  class="w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-black transition-all"
                  :class="armedTrackId === track.id
                    ? 'bg-red-500 text-white shadow-sm shadow-red-500/40'
                    : 'bg-slate-200 text-slate-400 hover:bg-red-100 hover:text-red-400'"
                  :disabled="isRecording"
                  v-tooltip.right="armedTrackId === track.id ? 'Disarm track' : 'Arm for recording'"
                  @click.stop="mixStore.armTrack(track.id)"
                >R</button>
                <i
                  v-if="bandStore.canEditMixes"
                  class="pi pi-sparkles text-slate-300 hover:text-teal-500 transition-colors cursor-pointer"
                  style="font-size: 0.975rem"
                  v-tooltip.right="'Generate with AI'"
                  @click.stop="openGenerateDialog(track.id)"
                ></i>
                <i
                  v-if="bandStore.canEditMixes"
                  class="pi pi-plus text-slate-300 hover:text-teal-500 transition-colors cursor-pointer"
                  style="font-size: 0.975rem"
                  v-tooltip.right="'Add clip at cursor'"
                  @click.stop="openAddClipDialog(track.id)"
                ></i>
              </div>
              <!-- Right column: Track Name (1/3) -->
              <div class="w-1/3 h-full flex items-center justify-center">
                <span class="text-[12.5px] font-medium text-slate-600 uppercase whitespace-nowrap tracking-tighter cursor-pointer hover:text-teal-600 transition-colors"
                      style="writing-mode: vertical-lr;"
                      @click.stop="openRenameTrack(track.id, track.name)">
                  {{ track.name }}
                </span>
              </div>
              </div>
              <!-- Real-time level meter on the border with the timeline -->
              <div class="h-2 shrink-0">
                <TrackMeter :track-id="track.id" horizontal />
              </div>
            </div>
          </div>
        </div>

        <!-- Scrollable Body -->
        <div class="flex-1 overflow-auto relative" id="tracks-container"
             @scroll="handleScroll"
             @mousemove="handleMouseMoveTimeline"
             @mouseleave="handleMouseLeaveTimeline">
          
          <!-- Hover Guide Line (horizontal, spans full track stack) -->
          <div v-if="hoverTime !== null"
               class="absolute h-px bg-slate-300 z-30 pointer-events-none"
               :style="hoverLineStyle"></div>
          <!-- Hover Timecode Label (kept centered in viewport) -->
          <div v-if="hoverTime !== null"
               class="absolute -translate-x-1/2 -translate-y-1/2 bg-white/90 text-slate-400 text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-100 shadow-sm whitespace-nowrap z-30 pointer-events-none"
               :style="hoverLabelStyle">
            {{ formatTime(hoverTime) }}
          </div>

          <!-- Playback Cursor (horizontal line, spans full track stack) -->
          <div class="absolute h-[2px] bg-teal-500 z-40 pointer-events-none"
               :style="cursorLineStyle"></div>
          <!-- Cursor Timecode Label (kept centered in viewport) -->
          <div class="absolute -translate-x-1/2 -translate-y-1/2 bg-slate-900 text-white text-[11px] font-mono px-2.5 py-1 rounded-lg shadow-2xl whitespace-nowrap pointer-events-none border border-slate-700 z-50"
               :style="cursorLabelStyle">
            {{ formatTime(currentTime) }}
          </div>
          <!-- Draggable Triangle (kept at left of viewport) -->
          <div
            class="absolute w-5 h-5 bg-teal-500 rotate-45 -translate-y-[9px] rounded-sm shadow-md pointer-events-auto cursor-grab active:cursor-grabbing z-50"
            :style="cursorHandleStyle"
            @mousedown="handleMouseDownCursor"
          ></div>

          <!-- min-w-full makes the wrapper stretch to at least the container width
               when there are few tracks, so the seek layer below covers the empty
               area to the right of the last column. Tracks still pack from the left. -->
          <div class="flex flex-row inline-flex relative min-h-full min-w-full">
            <div v-if="!mixDetails?.tracks.length" class="absolute inset-0 flex flex-col items-center justify-center text-slate-300 min-w-[300px]">
              <i class="pi pi-list text-5xl mb-4 opacity-20"></i>
              <p class="text-sm italic">No tracks yet.</p>
            </div>

            <!-- Always-on seek layer covering the full track stack -->
            <div class="absolute inset-0 z-10 cursor-pointer" @click="handleTimelineClick"></div>

            <TimelineTrack
              v-for="track in mixDetails?.tracks"
              :key="track.id"
              :track="track"
              :mix-duration-seconds="mixDetails?.durationSeconds || 300"
              :readonly="!bandStore.canEditMixes"
              class="relative z-20"
            />
          </div>
        </div>
      </div>

      <!-- MIXER VIEW (phone only, below timeline when toggled on) -->
      <div v-if="!isHorizontal && mixerVisible" class="shrink-0 h-[32vh] flex flex-col p-4 bg-slate-50/30 overflow-y-auto border-t border-slate-200">
        <div v-if="!mixDetails?.tracks.length" class="flex-1 flex flex-col items-center justify-center text-slate-300">
          <i class="pi pi-sliders-h text-5xl mb-4 opacity-20"></i>
          <p class="text-sm italic">No tracks in this mix.</p>
        </div>
        <template v-else>
          <!-- Master Section -->
          <div class="mb-6 p-3 bg-white rounded-2xl border border-slate-300 shadow-sm flex items-center gap-2">
             <p class="rd-label text-slate-900 shrink-0">Master</p>
             <div class="flex-1 h-4 bg-slate-100 rounded-full border border-slate-400 overflow-hidden relative">
                <div
                  class="h-full bg-teal-500 transition-all duration-75"
                  :style="{ width: `${masterLevel * 100}%` }"
                ></div>
             </div>
             <p class="text-[10px] font-mono text-slate-500 text-right font-bold whitespace-nowrap">{{ masterDb }} dB</p>
          </div>

          <div class="flex flex-col gap-2">
            <MixerChannel
              v-for="track in mixDetails.tracks"
              :key="track.id"
              :track="track"
              :readonly="!bandStore.canEditMixes"
            />
          </div>
        </template>
      </div>

    </div>

    <!-- Rename Track Dialog -->
    <Dialog v-model:visible="showRenameTrack" modal header="Rename Track" :style="{ width: '22rem' }">
      <div class="flex flex-col gap-4 py-2">
        <div class="flex flex-col gap-2">
          <label for="renameTrackInput" class="font-semibold text-sm">Track Name</label>
          <InputText id="renameTrackInput" v-model="renameTrackName" class="w-full" autocomplete="off" @keyup.enter="handleRenameTrack" />
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <Button type="button" label="Cancel" severity="secondary" @click="showRenameTrack = false" />
          <Button type="button" label="Rename" :loading="isRenaming" @click="handleRenameTrack" />
        </div>
      </div>
    </Dialog>

    <!-- Add Clip Source Picker (Library / Upload) -->
    <Dialog v-model:visible="showAddClipSourceDialog" modal header="Add clip" :style="{ width: '22rem' }">
      <p class="text-sm text-slate-600 mb-4">Where do you want the clip from?</p>
      <div class="flex flex-col gap-2">
        <Button type="button" label="Pick from library" icon="pi pi-bookmark" class="w-full" @click="handlePickSourceLibrary" />
        <Button type="button" label="Upload audio file" icon="pi pi-folder-open" severity="secondary" class="w-full" @click="handlePickSourceUpload" />
      </div>
    </Dialog>

    <!-- Library Picker -->
    <Dialog v-model:visible="showLibraryPickerDialog" modal header="Pick from your library" :style="{ width: '32rem' }">
      <div class="flex flex-col gap-2 py-1">
        <div v-if="userLibraryStore.loading" class="text-xs text-slate-400 italic"><i class="pi pi-spin pi-spinner mr-1"></i> Loading…</div>
        <div v-else-if="userLibraryStore.clips.length === 0" class="text-xs text-slate-400 italic bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
          Your clip library is empty. Save clips to your library from a clip's download dialog.
        </div>
        <ul v-else class="divide-y divide-slate-200 max-h-80 overflow-y-auto">
          <li v-for="lc in userLibraryStore.clips" :key="lc.id">
            <button class="w-full flex items-center gap-3 px-2 py-2 hover:bg-slate-50 text-left transition-colors rounded" :disabled="addingFromLibrary" @click="handleAddFromLibrary(lc.id)">
              <i class="pi pi-sparkles text-teal-500 shrink-0" style="font-size: 0.85rem"></i>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-semibold text-slate-900 truncate">{{ lc.name }}</div>
                <div class="text-[10px] text-slate-400">
                  AI {{ lc.aiInstrument }} · {{ lc.bpm }} BPM · {{ lc.duration.toFixed(1) }}s
                </div>
              </div>
              <i v-if="addingFromLibrary" class="pi pi-spin pi-spinner text-slate-400"></i>
            </button>
          </li>
        </ul>
        <div class="flex justify-end mt-2">
          <Button type="button" label="Cancel" severity="secondary" @click="showLibraryPickerDialog = false" :disabled="addingFromLibrary" />
        </div>
      </div>
    </Dialog>

    <!-- Track Download Format Dialog -->
    <Dialog v-model:visible="showTrackDownloadDialog" modal header="Download track" :style="{ width: '22rem' }">
      <p class="text-sm text-slate-600 mb-4">
        This track contains AI-generated clips with MIDI data. How would you like to download it?
      </p>
      <div class="flex gap-2">
        <Button type="button" label="MIDI" icon="pi pi-code" severity="secondary" class="flex-1" @click="handleTrackDownloadChoice('midi')" />
        <Button type="button" label="WAV" icon="pi pi-volume-up" class="flex-1" @click="handleTrackDownloadChoice('wav')" />
      </div>
    </Dialog>

    <!-- MIDI JSON Paste-and-Render Dialog -->
    <Dialog v-model:visible="showMidiJsonDialog" modal header="Render MIDI JSON" :style="{ width: '40rem' }">
      <div class="flex flex-col gap-3 py-2">
        <div class="text-[11px] text-slate-500">
          Paste a MIDI JSON object (the kind various LLMs produce for drum generation).
          Format: <code>{ "bpm": 120, "notes": [[pitch, time, velocity], ...] }</code> —
          3-element array for drums, 4-element <code>[pitch, time, duration, velocity]</code> for pitched instruments.
          Will be rendered as drums and dropped on the active track at the cursor.
        </div>
        <Textarea v-model="devMidiJsonText" rows="14" class="w-full text-[11px] font-mono"
                  placeholder='{"bpm": 120, "notes": [[36, 0.0, 100], [38, 0.5, 100], ...]}' />
        <div v-if="devMidiJsonError" class="text-xs text-red-600 bg-red-50 rounded-lg p-2 border border-red-200">
          {{ devMidiJsonError }}
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <Button type="button" label="Cancel" severity="secondary" @click="showMidiJsonDialog = false" />
          <Button type="button" label="Render" icon="pi pi-play"
                  :disabled="!devMidiJsonText.trim() || devMidiJsonRendering || generateTrackId === null"
                  :loading="devMidiJsonRendering" @click="handleRenderMidiJson" />
        </div>
      </div>
    </Dialog>

    <!-- StepGrid Paste-and-Render Dialog -->
    <Dialog v-model:visible="showStepGridDialog" modal header="Render StepGrid" :style="{ width: '46rem' }">
      <div class="flex flex-col gap-3 py-2">
        <div class="text-[11px] text-slate-500">
          Paste a step-grid drum pattern (one row per drum, space-separated steps).
          Letters: <code>K</code>=Kick · <code>S</code>=Snare · <code>X</code>=Side-stick ·
          <code>H</code>=Closed Hat · <code>O</code>=Open Hat · <code>P</code>=Pedal Hat ·
          <code>T</code>=Tom · <code>C</code>=Crash · <code>R</code>=Ride.
          Use <code>X</code>=accent, <code>x</code>=hit, <code>.</code>=rest. 16 steps per bar at 16th-note resolution.
          Bars × BPM × time signature come from the mix.
        </div>
        <div class="flex items-center gap-2">
          <label class="text-[10px] text-slate-500">Bars</label>
          <input type="number" v-model.number="devStepGridBars" min="1" max="24" class="w-14 text-xs border border-slate-300 rounded px-2 py-1.5" />
          <span class="text-[10px] text-slate-400 ml-auto">Will be rendered to the active AI Dev track at the cursor.</span>
        </div>
        <Textarea v-model="devStepGridText" rows="10" class="w-full text-[11px] font-mono"
                  :placeholder="'K x . . . x . . . x . . . x . . .\nS . . . . X . . . . . . . X . . .\nH x x x x x x x x x x x x x x x x'" />
        <div v-if="devStepGridError" class="text-xs text-red-600 bg-red-50 rounded-lg p-2 border border-red-200">
          {{ devStepGridError }}
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <Button type="button" label="Cancel" severity="secondary" @click="showStepGridDialog = false" />
          <Button type="button" label="Render" icon="pi pi-play"
                  :disabled="!devStepGridText.trim() || devStepGridRendering || generateTrackId === null"
                  :loading="devStepGridRendering" @click="handleRenderStepGrid" />
        </div>
      </div>
    </Dialog>

    <!-- Test Prompt Result Dialog -->
    <Dialog v-model:visible="showTestPromptDialog" modal header="Test Prompt Result" :style="{ width: '52rem' }">
      <div v-if="devTestPromptResult" class="flex flex-col gap-3 py-2">
        <div class="flex flex-col gap-1">
          <label class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
            Retrieved {{ devTestPromptResult.retrievedLoops.length }} loop{{ devTestPromptResult.retrievedLoops.length === 1 ? '' : 's' }}
            <span v-if="devTestPromptResult.resolvedFolderPrefix" class="ml-2 text-teal-700 font-mono normal-case tracking-normal">
              ↳ folder picked: <span class="font-bold">{{ devTestPromptResult.resolvedFolderPrefix }}</span>
            </span>
          </label>
          <div v-if="devTestPromptResult.retrievedLoops.length > 0" class="bg-slate-50 border border-slate-200 rounded-lg p-2 max-h-48 overflow-y-auto">
            <ul class="space-y-1 font-mono text-[11px]">
              <li v-for="(loop, idx) in devTestPromptResult.retrievedLoops" :key="loop.filename" class="flex items-start gap-2">
                <span class="text-teal-700 font-bold w-8 shrink-0 text-right">#{{ idx + 1 }}</span>
                <span class="text-slate-700">
                  <span class="font-bold">{{ loop.filename }}</span>
                  <span class="text-slate-500">— {{ loop.genre }} {{ loop.sectionHint }} {{ loop.bpm }}bpm</span>
                </span>
              </li>
            </ul>
          </div>
          <div v-else class="text-xs text-slate-400 italic bg-slate-50 border border-slate-200 rounded-lg p-3">
            No loops retrieved — prompt may have no matching keywords, or library is empty.
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Final prompt sent to Gemini</label>
          <Textarea readonly :value="devTestPromptResult.promptText" rows="20" class="w-full text-[11px] font-mono" />
        </div>

        <div class="flex justify-end gap-2 mt-2">
          <Button type="button" label="Close" severity="secondary" @click="showTestPromptDialog = false" />
        </div>
      </div>
    </Dialog>

    <!-- JSON-vs-Grid Render Compare Dialog -->
    <Dialog v-model:visible="showCompareDialog" modal header="Render Compare: JSON vs StepGrid" :style="{ width: '76rem' }">
      <div v-if="devCompareResult" class="flex flex-col gap-4 py-2">
        <p class="text-[11px] text-slate-500">
          Same prompt run through both pipelines. Listen to each rendered audio and compare the LLM output text and resulting MIDI JSON.
        </p>
        <div class="grid grid-cols-2 gap-4">
          <div v-for="side in [devCompareResult.json, devCompareResult.grid].filter(Boolean)" :key="side.format" class="flex flex-col gap-2 border border-slate-200 rounded-lg p-3">
            <div class="flex items-center justify-between">
              <span class="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border" :class="side.format === 'json' ? 'bg-sky-100 text-sky-700 border-sky-200' : 'bg-amber-100 text-amber-700 border-amber-200'">
                {{ side.format === 'json' ? 'JSON → MIDI' : 'StepGrid → MIDI' }}
              </span>
              <span class="text-[10px] text-slate-500 font-mono">
                in {{ side.inputTokens }} · out {{ side.outputTokens }}<template v-if="side.cost != null"> · ${{ side.cost.toFixed(4) }}</template>
              </span>
            </div>
            <div v-if="side.errorMessage" class="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {{ side.errorMessage }}
            </div>
            <template v-else>
              <audio :src="compareAudioSrc(side.audioBase64)" controls class="w-full" />
              <details>
                <summary class="text-[10px] font-bold text-slate-500 uppercase tracking-widest cursor-pointer">Raw LLM response</summary>
                <Textarea readonly :value="side.responseText" rows="10" class="w-full text-[10px] font-mono mt-1" />
              </details>
              <details>
                <summary class="text-[10px] font-bold text-slate-500 uppercase tracking-widest cursor-pointer">Final MIDI JSON</summary>
                <Textarea readonly :value="prettyJson(side.midiJson)" rows="10" class="w-full text-[10px] font-mono mt-1" />
              </details>
              <details>
                <summary class="text-[10px] font-bold text-slate-500 uppercase tracking-widest cursor-pointer">Prompt sent</summary>
                <Textarea readonly :value="side.promptText" rows="14" class="w-full text-[10px] font-mono mt-1" />
              </details>
            </template>
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <Button type="button" label="Close" severity="secondary" @click="showCompareDialog = false" />
        </div>
      </div>
    </Dialog>

    <!-- Mix Info Dialog -->
    <Dialog v-model:visible="showMixInfo" modal header="Mix Info" :style="{ width: '24rem' }">
      <div class="flex flex-col gap-4 py-2">
        <div class="flex flex-col gap-2">
          <label for="mixNameInput" class="font-semibold text-sm">Mix Name</label>
          <InputText id="mixNameInput" v-model="editMixName" class="w-full" autocomplete="off" @keyup.enter="handleMixInfoSave" />
        </div>
        <div class="flex flex-col gap-2">
          <label class="font-semibold text-sm">Duration</label>
          <div class="flex items-center justify-center gap-2">
            <div class="flex flex-col items-center gap-1">
              <Button icon="pi pi-plus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="nudgeMixField('mins', 1)" />
              <InputText v-model="editMixMins" class="!w-14 text-center font-mono !p-1" maxlength="2" @blur="handleMixDurationFieldBlur" @keydown.enter="handleMixDurationFieldBlur" @keydown="($event: KeyboardEvent) => { if (!/\d/.test($event.key) && !['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Enter','Home','End'].includes($event.key)) $event.preventDefault() }" />
              <Button icon="pi pi-minus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="nudgeMixField('mins', -1)" />
              <span class="text-[8.5px] font-bold text-slate-500 uppercase tracking-widest mt-1">Minutes</span>
            </div>
            <span class="font-mono text-2xl font-bold pb-7">:</span>
            <div class="flex flex-col items-center gap-1">
              <Button icon="pi pi-plus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="nudgeMixField('secs', 1)" />
              <InputText v-model="editMixSecs" class="!w-14 text-center font-mono !p-1" maxlength="2" @blur="handleMixDurationFieldBlur" @keydown.enter="handleMixDurationFieldBlur" @keydown="($event: KeyboardEvent) => { if (!/\d/.test($event.key) && !['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Enter','Home','End'].includes($event.key)) $event.preventDefault() }" />
              <Button icon="pi pi-minus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="nudgeMixField('secs', -1)" />
              <span class="text-[8.5px] font-bold text-slate-500 uppercase tracking-widest mt-1">Seconds</span>
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <label class="font-semibold text-sm">BPM</label>
            <Button icon="pi pi-stopwatch" :severity="dialogMetronomeOn ? 'warn' : 'secondary'" :text="!dialogMetronomeOn" rounded size="small" class="w-8 h-8" v-tooltip.left="dialogMetronomeOn ? 'Stop preview' : 'Preview metronome'" @click="toggleDialogMetronome" />
          </div>
          <div class="flex items-center justify-center gap-2">
            <Button icon="pi pi-minus" severity="secondary" text rounded size="small" class="!w-8 !h-8 !p-0 shrink-0" @click="nudgeBpm(-1)" />
            <InputText v-model="editMixBpm" class="!w-20 text-center font-mono font-bold !p-1" maxlength="3" @blur="handleBpmBlur" @keydown.enter="handleBpmBlur" @keydown="($event: KeyboardEvent) => { if (!/\d/.test($event.key) && !['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Enter','Home','End'].includes($event.key)) $event.preventDefault() }" />
            <Button icon="pi pi-plus" severity="secondary" text rounded size="small" class="!w-8 !h-8 !p-0 shrink-0" @click="nudgeBpm(1)" />
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <label for="mixTimeSigInput" class="font-semibold text-sm">Time Signature</label>
          <InputText id="mixTimeSigInput" v-model="editMixTimeSig" class="w-full" placeholder="e.g. 4/4" autocomplete="off" />
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <Button type="button" label="Cancel" severity="secondary" @click="showMixInfo = false" />
          <Button type="button" label="Save" :loading="isSavingMixInfo" @click="handleMixInfoSave" />
        </div>
      </div>
    </Dialog>

    <!-- AI Generate Dialog -->
    <Dialog v-model:visible="showGenerateDialog" modal header="Generate with AI" :style="{ width: generateEngine === 'dev' && devClassifyResults.length >= 3 ? '76rem' : (generateEngine === 'dev' && devClassifyResults.length === 2 ? '52rem' : '28rem') }">
      <div class="flex flex-col gap-4 py-2">
        <div v-if="mixStore.generateError" class="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {{ mixStore.generateError }}
        </div>

        <!-- Engine Selection -->
        <div class="flex gap-2">
          <button
            class="flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all border"
            :class="generateEngine === 'drums' ? 'bg-teal-500 text-white border-teal-600' : 'bg-slate-50 text-slate-500 border-slate-300 hover:bg-slate-100'"
            @click="generateEngine = 'drums'"
          >AI Drums</button>
          <button
            class="flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all border"
            :class="generateEngine === 'piano' ? 'bg-teal-500 text-white border-teal-600' : 'bg-slate-50 text-slate-500 border-slate-300 hover:bg-slate-100'"
            @click="generateEngine = 'piano'"
          >AI Piano</button>
          <button
            class="flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all border"
            :class="generateEngine === 'bass' ? 'bg-teal-500 text-white border-teal-600' : 'bg-slate-50 text-slate-500 border-slate-300 hover:bg-slate-100'"
            @click="generateEngine = 'bass'"
          >AI Bass</button>
          <button
            class="flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all border"
            :class="generateEngine === 'dev' ? 'bg-amber-500 text-white border-amber-600' : 'bg-slate-50 text-slate-500 border-slate-300 hover:bg-slate-100'"
            @click="generateEngine = 'dev'"
          >AI Dev</button>
        </div>

        <!-- AI Dev experimental panel -->
        <template v-if="generateEngine === 'dev'">
          <div class="flex flex-col gap-2">
            <div class="flex gap-2">
              <Button type="button" label="Browse MIDI file..." icon="pi pi-folder-open" severity="secondary" class="flex-1" @click="devPickFile" />
              <Button type="button" label="MIDI JSON" icon="pi pi-code" severity="secondary" class="flex-1" @click="openMidiJsonDialog" />
              <Button type="button" label="StepGrid" icon="pi pi-th-large" severity="secondary" class="flex-1" @click="openStepGridDialog" />
            </div>
            <div v-if="devParsedMidi" class="text-[11px] text-slate-600 bg-slate-50 rounded-lg p-2 border border-slate-200 font-mono">
              <div><b>{{ devParsedMidi.filename }}</b></div>
              <div>BPM {{ devParsedMidi.bpm }} · {{ devParsedMidi.timeSignature }} · {{ devParsedMidi.bars }} bars · {{ devParsedMidi.noteCount }} notes · {{ devParsedMidi.durationSeconds.toFixed(2) }}s</div>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <Button type="button" label="Classify" icon="pi pi-sparkles" :disabled="!devParsedMidi || devClassifying" :loading="devClassifying" @click="devClassify" />
            <Button type="button" label="Render" icon="pi pi-play" severity="secondary" :disabled="!devParsedMidi || devRendering || generateTrackId === null" :loading="devRendering" @click="devRender" />
          </div>
          <div class="flex items-center gap-4 flex-wrap">
            <label class="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
              <input type="checkbox" v-model="devCompareWithProfile" class="w-4 h-4 accent-amber-500 cursor-pointer" />
              Compare with profile model
            </label>
            <label class="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
              <input type="checkbox" v-model="devCompareWithLocal" class="w-4 h-4 accent-amber-500 cursor-pointer" />
              Compare with local AI
            </label>
          </div>

          <div v-if="devError" class="text-xs text-red-600 bg-red-50 rounded-lg p-2 border border-red-200">{{ devError }}</div>

          <div v-if="devClassifyResults.length > 0" class="grid gap-3" :class="devClassifyResults.length === 3 ? 'grid-cols-3' : (devClassifyResults.length === 2 ? 'grid-cols-2' : 'grid-cols-1')">
            <div v-for="(pane, idx) in devClassifyResults" :key="idx" class="flex flex-col gap-1">
              <label class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{{ pane.label }}</label>
              <Textarea readonly :value="pane.responseText" rows="10" class="w-full text-xs font-mono" />
              <div class="text-[10px] text-slate-500 flex items-center justify-between gap-2 px-1">
                <span>{{ pane.aiModel }} · in {{ pane.inputTokens }} · out {{ pane.outputTokens }}</span>
                <span v-if="pane.cost != null" class="font-mono">${{ pane.cost.toFixed(4) }}</span>
              </div>
            </div>
          </div>

          <!-- Drum prompt preview / compare: assembles the final prompt that would be sent to Gemini for AI Drums (RAG examples included). Test Prompt = preview only (no Gemini call). Render Compare = runs both pipelines (JSON output vs Step-Grid output), parses, renders to audio, and shows side-by-side. -->
          <div class="flex flex-col gap-2 pt-2 border-t border-slate-200">
            <label class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Test drum prompt</label>
            <Textarea v-model="devTestPrompt" placeholder="e.g. funky verse with side stick" rows="3" class="w-full text-xs" @keydown.ctrl.enter="devTestPromptHandler" />
            <div class="flex items-center gap-2 flex-wrap">
              <label class="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer select-none">
                <input type="checkbox" v-model="devTestUseLibrary" class="w-4 h-4 accent-teal-500 cursor-pointer" />
                Genre & style influence
              </label>
              <span class="text-slate-300">|</span>
              <label class="text-[10px] text-slate-500">Bars</label>
              <input type="number" v-model.number="devTestBars" min="1" max="24" class="w-14 text-xs border border-slate-300 rounded px-2 py-1.5" />
              <span class="text-slate-300">|</span>
              <label class="text-[10px] text-slate-500">Format</label>
              <select v-model="devTestFormat" class="text-xs border border-slate-300 rounded px-2 py-1.5 bg-white">
                <option value="json">JSON</option>
                <option value="grid">StepGrid</option>
              </select>
              <span class="text-[10px] text-slate-400 ml-auto">Ctrl+Enter</span>
              <Button type="button" label="Test Prompt" icon="pi pi-search" severity="secondary" :disabled="!devTestPrompt.trim() || devTestingPrompt" :loading="devTestingPrompt" @click="devTestPromptHandler" />
              <Button type="button" label="Render Compare" icon="pi pi-play" :disabled="!devTestPrompt.trim() || devCompareRunning" :loading="devCompareRunning" @click="devRenderCompareHandler" v-tooltip.top="'Run both JSON→MIDI and Grid→MIDI pipelines and play side by side'" />
            </div>
          </div>
        </template>

        <!-- Prompt -->
        <div v-if="generateEngine !== 'dev'" class="flex flex-col gap-2">
          <label for="generatePromptInput" class="font-semibold text-sm">{{ generateEngine === 'piano' ? 'Describe the piano part you want' : generateEngine === 'bass' ? 'Describe the bass line you want' : 'Describe the drum pattern you want' }}</label>
          <select class="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-slate-50 text-slate-500 cursor-pointer w-full" @change="(e: any) => { if (e.target.value) { generatePrompt = e.target.value; e.target.value = '' } }">
            <option value="">Examples...</option>
            <option v-for="example in (generateEngine === 'drums' ? drumPromptExamples : generateEngine === 'bass' ? bassPromptExamples : pianoPromptExamples)" :key="example" :value="example">{{ example.length > 60 ? example.slice(0, 60) + '...' : example }}</option>
          </select>
          <Textarea id="generatePromptInput" v-model="generatePrompt" rows="4" class="w-full text-sm" :placeholder="generateEngine === 'piano' ? 'e.g. Jazzy Rhodes comping in C minor with lush 7th chords and syncopated rhythm' : generateEngine === 'bass' ? 'e.g. Funky slap bass in A minor with syncopated 16ths and ghost notes' : 'e.g. Hard rock beat straight no transitions just kick snare and high-hat'" @keydown.ctrl.enter="handleGenerate" />
        </div>

        <!-- Bars -->
        <div v-if="generateEngine !== 'dev'" class="flex items-center gap-3">
          <label class="font-semibold text-sm whitespace-nowrap">Bars</label>
          <input type="range" v-model.number="generateBars" min="1" max="24" step="1"
            class="flex-1 h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-500" />
          <span class="text-sm font-mono text-slate-600 w-6 text-right">{{ generateBars }}</span>
        </div>

        <!-- Use Clip Reference (Drums only) -->
        <div v-if="generateEngine === 'drums' && generateTrackId !== null" class="flex flex-col gap-2">
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-2">
              <input id="useDrumLibraryReference" type="checkbox" v-model="useDrumLibraryReference"
                class="w-4 h-4 accent-teal-500 cursor-pointer" />
              <label for="useDrumLibraryReference" class="text-sm cursor-pointer select-none text-slate-700">
                Genre & style influence
              </label>
            </div>
            <div class="flex items-center gap-2">
              <input id="useDrumReference" type="checkbox" v-model="useDrumReference"
                class="w-4 h-4 accent-teal-500 cursor-pointer"
                :disabled="!mixStore.currentMix?.tracks.find(t => t.id === generateTrackId)?.clips.length" />
              <label for="useDrumReference" class="text-sm cursor-pointer select-none"
                :class="mixStore.currentMix?.tracks.find(t => t.id === generateTrackId)?.clips.length ? 'text-slate-700' : 'text-slate-400'">
                Use clip as a reference
              </label>
            </div>
          </div>
          <select v-if="useDrumReference" v-model="drumReferenceClipId"
            class="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-slate-50 text-slate-700 cursor-pointer w-full">
            <option :value="null">Select a clip...</option>
            <option v-for="clip in mixStore.currentMix?.tracks.find(t => t.id === generateTrackId)?.clips || []" :key="clip.id" :value="clip.id">
              {{ clip.name || `Clip @ ${clip.timelineStart.toFixed(1)}s` }} ({{ clip.duration.toFixed(1) }}s)
            </option>
          </select>
          <span v-if="useDrumReference" class="text-[10px] text-slate-400">
            Clip audio is sent to the AI to inform tempo, groove, and accents
          </span>
        </div>

        <!-- Use Clip as Reference (Piano only) -->
        <div v-if="generateEngine === 'piano' && generateTrackId !== null" class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <input id="usePianoReference" type="checkbox" v-model="usePianoReference"
              class="w-4 h-4 accent-teal-500 cursor-pointer"
              :disabled="!mixStore.currentMix?.tracks.find(t => t.id === generateTrackId)?.clips.length" />
            <label for="usePianoReference" class="text-sm cursor-pointer select-none"
              :class="mixStore.currentMix?.tracks.find(t => t.id === generateTrackId)?.clips.length ? 'text-slate-700' : 'text-slate-400'">
              Use clip as a reference
            </label>
          </div>
          <select v-if="usePianoReference" v-model="pianoReferenceClipId"
            class="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-slate-50 text-slate-700 cursor-pointer w-full">
            <option :value="null">Select a clip...</option>
            <option v-for="clip in mixStore.currentMix?.tracks.find(t => t.id === generateTrackId)?.clips || []" :key="clip.id" :value="clip.id">
              {{ clip.name || `Clip @ ${clip.timelineStart.toFixed(1)}s` }} ({{ clip.duration.toFixed(1) }}s)
            </option>
          </select>
          <span v-if="usePianoReference" class="text-[10px] text-slate-400">
            Clip audio is sent to the AI to inform tempo, key, harmony, and feel
          </span>
        </div>

        <!-- Use Clip as Reference (Bass only) -->
        <div v-if="generateEngine === 'bass' && generateTrackId !== null" class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <input id="useBassReference" type="checkbox" v-model="useBassReference"
              class="w-4 h-4 accent-teal-500 cursor-pointer"
              :disabled="!mixStore.currentMix?.tracks.find(t => t.id === generateTrackId)?.clips.length" />
            <label for="useBassReference" class="text-sm cursor-pointer select-none"
              :class="mixStore.currentMix?.tracks.find(t => t.id === generateTrackId)?.clips.length ? 'text-slate-700' : 'text-slate-400'">
              Use clip as a reference
            </label>
          </div>
          <select v-if="useBassReference" v-model="bassReferenceClipId"
            class="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-slate-50 text-slate-700 cursor-pointer w-full">
            <option :value="null">Select a clip...</option>
            <option v-for="clip in mixStore.currentMix?.tracks.find(t => t.id === generateTrackId)?.clips || []" :key="clip.id" :value="clip.id">
              {{ clip.name || `Clip @ ${clip.timelineStart.toFixed(1)}s` }} ({{ clip.duration.toFixed(1) }}s)
            </option>
          </select>
          <span v-if="useBassReference" class="text-[10px] text-slate-400">
            Clip audio is sent to the AI to inform tempo, key, groove, and feel
          </span>
        </div>

        <div class="flex items-center justify-between mt-2">
          <span v-if="generateEngine !== 'dev'" class="text-[10px] text-slate-400">Ctrl+Enter to generate</span>
          <span v-else class="text-[10px] text-slate-400">Experimental: classify or render uploaded MIDI</span>
          <div class="flex gap-2">
            <Button type="button" label="Close" severity="secondary" @click="showGenerateDialog = false; mixStore.generateError = ''" />
            <Button v-if="generateEngine !== 'dev'" type="button" label="Generate" icon="pi pi-sparkles"
              :disabled="!generatePrompt.trim()"
              @click="handleGenerate" />
          </div>
        </div>
      </div>
    </Dialog>

    <!-- Track Delete Confirmation Dialog -->
    <Dialog v-model:visible="showDeleteTrackConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-exclamation-triangle text-red-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Are you sure?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will permanently delete the track <strong>"{{ trackToDelete?.name }}"</strong> and all its audio clips.</p>
        
        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Delete Track" severity="danger" class="w-full py-3 font-bold" @click="handleDeleteTrack" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showDeleteTrackConfirm = false" />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
/* Custom scrollbar to match app style */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}

:global(.dark-mode) ::-webkit-scrollbar-thumb {
  background: #334155;
}
:global(.dark-mode) ::-webkit-scrollbar-thumb:hover {
  background: #475569;
}
</style>
